import { useMemo } from "react";
import { useAppContext } from "@/context/context";
import { useGetApiKeysQuery } from "@/services/authApi";
import {
  useFetchAlertSchemasQuery,
  useFetchAllChecksQuery,
  useFetchPortfoliosQuery,
  useFetchSlackChannelsQuery,
  useFetchTelegramChannelsQuery,
  useFetchSmsChannelsQuery,
  useFetchGroupDestinationsQuery,
} from "../services/alertManagerApi";
import { mapChecksToRow } from "../utils/parsers";
import { IGroupDestinations } from "../AlertManager.types";

export const useAlertManager = () => {
  const { context_state } = useAppContext();
  const portfoliosMetadata = context_state?.org_metadata?.portfolios;

  const { data: schemas } = useFetchAlertSchemasQuery();
  const { data: checksData, isLoading: isLoadingChecks } =
    useFetchAllChecksQuery();
  const { data: portfolios } = useFetchPortfoliosQuery();
  const { data: slackChannels } = useFetchSlackChannelsQuery();
  const { data: telegramChannels } = useFetchTelegramChannelsQuery();
  const { data: smsChannels } = useFetchSmsChannelsQuery();
  const { data: groupDestinations } = useFetchGroupDestinationsQuery();
  const { data: apiKeys } = useGetApiKeysQuery();

  const rowData = useMemo(
    () => (checksData ? mapChecksToRow(checksData, portfoliosMetadata) : []),
    [checksData, portfoliosMetadata]
  );

  const groupDestinationNames = useMemo(
    () =>
      (groupDestinations ?? []).map(
        (g: IGroupDestinations) => g.group_name
      ),
    [groupDestinations]
  );

  const portfolioSelectOptions = useMemo(
    () =>
      (portfolios ?? []).map((p: string) => ({ label: p, value: p })),
    [portfolios]
  );

  const sortedApiKeys = useMemo(
    () => (apiKeys ?? []).slice().sort((a, b) => a.key_id.localeCompare(b.key_id)),
    [apiKeys]
  );

  return {
    schemas: schemas?.alert_checks,
    rowData,
    apiKeys: sortedApiKeys,
    portfolios: portfolios ?? [],
    portfolioSelectOptions,
    slackChannels: slackChannels ?? [],
    telegramChannels: telegramChannels ?? [],
    smsChannels: smsChannels ?? [],
    groupDestinationNames,
    loading: isLoadingChecks,
  };
};
